import { type ReactNode } from 'react';
import { Modal as MantineModal, type ModalProps as MantineModalProps } from '@mantine/core';

export interface ForgeModalProps
  extends Omit<MantineModalProps, 'children' | 'opened' | 'onClose'> {
  opened: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Footer slot (typically primary + cancel buttons). */
  footer?: ReactNode;
  children?: ReactNode;
}

/**
 * Modal — focus-trapped dialog with dark-glass backdrop.
 *
 * Mantine handles the focus-trap, ESC-to-close, and aria semantics — Forge
 * just themes the surface to match the void/neon palette. Use `Drawer` for
 * side-anchored panels (filter sheets, detail inspectors); use `Modal` for
 * confirmations and short forms.
 */
export function Modal({
  opened,
  onClose,
  title,
  footer,
  children,
  size = 'md',
  centered = true,
  ...rest
}: ForgeModalProps) {
  return (
    <MantineModal
      opened={opened}
      onClose={onClose}
      title={title}
      size={size}
      centered={centered}
      radius="md"
      overlayProps={{
        backgroundOpacity: 0.72,
        blur: 6,
        color: '#09090b',
      }}
      styles={{
        content: {
          backgroundColor: 'rgb(var(--c-card))',
          border: '1px solid var(--c-bd)',
        },
        header: {
          backgroundColor: 'rgb(var(--c-card))',
          borderBottom: '1px solid var(--c-bd)',
          fontWeight: 600,
        },
        body: {
          padding: 20,
        },
      }}
      trapFocus
      closeOnEscape
      withCloseButton
      {...rest}
    >
      {children}
      {footer ? (
        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: '1px solid var(--c-bd)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          {footer}
        </div>
      ) : null}
    </MantineModal>
  );
}
